import React, { ReactNode, useEffect, useState } from 'react';
import Hammer from 'react-hammerjs';
import styled from 'styled-components';

import { Box } from 'components/Box';
import Flex from 'components/Flex';
import { bp } from 'theme';
import { isBrowser } from 'utils/browser';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const Slide = ({ children, ...props }: { children: ReactNode }) => (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Box {...props}>{children}</Box>
);
const Slider = styled(
  ({
    dataQaPrefix,
    pageLength,
    children,
    className,
    gutter,
  }: {
    dataQaPrefix: string;
    pageLength: number;
    children: Array<React.ReactElement<typeof Slide>>;
    className?: string;
    gutter: number;
  }) => (
    <Flex
      className={className}
      data-qa-slide-count={children.length}
      data-qa-id={`${dataQaPrefix}-slider`}
    >
      {children.map((child, index) => (
        <Box
          data-qa-id={`${dataQaPrefix}-slide-${index}`}
          width={`calc((100% - ${
            (pageLength - 1) * gutter
          }px) / ${pageLength})`}
          ml={pageLength > 1 ? `${gutter}px` : 0} // eslint-disable-next-line react/no-array-index-key
          key={index}
          flex="0 0 auto"
        >
          {child}
        </Box>
      ))}
    </Flex>
  )
)<{ transform: string }>`
  width: 100%;
  flex-wrap: nowrap;
  transform: ${({ transform }) => transform};
  transition: transform 0.5s cubic-bezier(0.075, 0.82, 0.165, 1);
  will-change: transform;
`;
const SliderContainer = styled.div`
  overflow-x: hidden;
`;

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition, react/jsx-no-useless-fragment
const Navigation = ({ children }: { children: ReactNode }) => <>{children}</>;

const CarouselContainer = styled.div`
  position: relative;
`;
type State = {
  current: number;
  total: number;
  lastPageLoaded: number;
  pageLength: number;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  goToPage: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nextPage: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  prevPage: (...args: Array<any>) => any;
};
type Props = {
  'data-qa-id': string;
  pageLength: Array<number> | number;
  slideCount?: Array<number> | number;
  slides: (arg0: State) => Array<React.ReactElement<typeof Slide>>;
  gutter?: number;
  navigation?: Array<(arg0: State) => ReactNode>;
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const Carousel = ({
  'data-qa-id': qaId,
  pageLength,
  slideCount,
  slides,
  gutter = 8,
  navigation,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [lastPageLoaded, setLastPageLoaded] = useState(0);
  const [currentPageLength, setCurrentPageLength] = useState(1);
  const [currentSlideCount, setCurrentSlideCount] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const updateCurrentPage = (index: number) => {
    setCurrentPage(index);
    setLastPageLoaded(Math.max(index, lastPageLoaded));
  };

  const goToPage = (index: number) => {
    updateCurrentPage(index);
  };

  const nextPage = () => {
    updateCurrentPage(Math.min(currentPage + 1, totalPages - 1));
  };

  const prevPage = () => {
    updateCurrentPage(Math.max(currentPage - 1, 0));
  };

  const slideArray = slides({
    current: currentPage,
    total: totalPages,
    goToPage,
    nextPage,
    prevPage,
    pageLength: currentPageLength,
    lastPageLoaded,
  });

  const resizeHandler = () => {
    const breakpoints = [0, ...bp];
    const windowWidth = isBrowser()
      ? window.innerWidth
      : breakpoints[breakpoints.length - 1];
    let currentBreakpoint = breakpoints.reduce(
      (acc, point, index) => (windowWidth >= point ? index : acc),
      0
    );

    if (
      pageLength instanceof Array &&
      currentBreakpoint > pageLength.length - 1
    ) {
      currentBreakpoint = pageLength.length - 1;
    }

    const newPageLength =
      pageLength instanceof Array ? pageLength[currentBreakpoint] : pageLength;
    const actualSlideCount =
      typeof slideCount === 'undefined' ? pageLength : slideCount;
    const newSlideCount =
      typeof actualSlideCount === 'number'
        ? actualSlideCount
        : Array(currentBreakpoint + 1)
            .fill(0)
            .reduce(
              (acc, breakpoint, index) =>
                typeof actualSlideCount[index] === 'undefined'
                  ? acc
                  : actualSlideCount[index],
              1
            );
    let newTotalPages = Math.ceil(
      (slideArray.length - currentPageLength) / currentSlideCount + 1
    );
    newTotalPages =
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-nested-ternary
      newTotalPages <= 0 ? 1 : newTotalPages > 99 ? 99 : newTotalPages || 1;
    setCurrentPageLength(newPageLength);
    setCurrentSlideCount(newSlideCount);
    setTotalPages(newTotalPages);
    updateCurrentPage(
      currentPage < newTotalPages - 1 ? currentPage : newTotalPages - 1
    );
  };

  const getTransform = () => {
    const multiplier = Math.max(
      Math.min(
        slideArray.length / currentPageLength - 1,
        currentPage * (currentSlideCount / currentPageLength)
      ),
      0
    );
    let transform = `translateX(-${multiplier * 100}%)`;
    if (currentPageLength > 1)
      transform += ` translateX(-${(multiplier + 1) * gutter}px)`;
    return transform;
  };

  const swipeHandler = ({ direction }: { direction: number }) => {
    if (direction === 2) {
      nextPage();
    } else if (direction === 4) {
      prevPage();
    }
  };

  const update = () => {
    resizeHandler();

    if (pageLength instanceof Array) {
      window.addEventListener('resize', resizeHandler);
    }
  };

  useEffect(() => {
    update();
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  });
  return (
    <CarouselContainer data-qa-id={qaId}>
      <Hammer onSwipe={swipeHandler}>
        <SliderContainer>
          <Slider
            dataQaPrefix={qaId}
            pageLength={currentPageLength}
            transform={getTransform()}
            gutter={gutter}
          >
            {slideArray}
          </Slider>
        </SliderContainer>
      </Hammer>
      {navigation &&
        navigation.map((nav, index) => (
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line react/no-array-index-key
          <Navigation key={index}>
            {nav({
              current: currentPage,
              total: totalPages,
              goToPage,
              nextPage,
              prevPage,
              pageLength: currentPageLength,
              lastPageLoaded,
            })}
          </Navigation>
        ))}
    </CarouselContainer>
  );
};

// eslint-disable-next-line import/no-default-export
export default Carousel;
